// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DropdownMenuSubMenu.tsx
 * @input React, stylex, useLayer (context mode), useListFocus, useMenuHover,
 *   useTypeahead, Item, Icon, Spinner, DropdownMenu context + item roles.
 * @output Exports DropdownMenuSubMenu — a single menu row that reveals a nested
 *   flyout menu of its own children/items.
 * @position Sub-component; place inside a DropdownMenu (or ContextMenu)
 *   alongside plain items.
 *
 * One component, not three. The row itself adopts DropdownMenuItem semantics
 * (label / icon / description / isDisabled) and its children become the
 * flyout's content. This mirrors how SideNavItem / TreeListItem promote a
 * normal row into a nested surface when given children, rather than the Radix
 * Sub / SubTrigger / SubContent split. Data-driven menus never touch this
 * component directly — renderDropdownItems renders it from a nested `items`
 * array and passes the rendered children in.
 *
 * Built on existing primitives — no bespoke floating code:
 * - Positioning: useLayer context mode opens the flyout inline-end with
 *   viewport auto-flip via CSS anchor positioning (RTL-correct by default).
 * - Pointer: useMenuHover for open/close intent.
 * - Keyboard: a per-level useListFocus + useTypeahead. Right (Left in RTL) /
 *   Enter / Space opens the flyout and focuses its first item; Left (Right in
 *   RTL) / Escape closes it and returns focus to the trigger row.
 *
 * Prior art: legacy internal XDS `XDSDropdownSubMenuItem` (APG menubar-
 * navigation submenu). This re-expresses the same contract on Astryx primitives.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/DropdownMenu/DropdownMenuSubMenu.doc.mjs
 * - /packages/core/src/DropdownMenu/DropdownMenuSubMenu.test.tsx
 * - /packages/core/src/DropdownMenu/index.ts
 * - /apps/storybook/stories/DropdownMenu.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/DropdownMenu/ (showcase blocks)
 */
import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { Item } from "../Item/index.js";
import { useLayer } from "../Layer/useLayer.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { useListFocus } from "../hooks/useListFocus.js";
import { useMenuHover } from "../hooks/useMenuHover.js";
import { useTypeahead } from "../hooks/useTypeahead.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, shadowVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { MENU_ITEM_ROLES, MENU_ITEM_SELECTOR, MENU_BOUNDARY_SELECTOR } from "./menuItemRoles.js";
import { DropdownMenuContext, useDropdownMenuContext } from "./DropdownMenuContext.js";
import { focusMenuItemOnHover } from "./menuItemHover.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const triggerStyles = {
  root: {
    kB7OPa: "x9f619",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xf314gf",
    kaIpWk: "x1ws5lxm",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w x1c52tdz",
    kkrTdU: "x1ypdohk",
    k9WMMc: "x1yc453h",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  open: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const triggerSizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    $$css: true
  },
  lg: {
    $$css: true
  }
};
const _temp = {
  keTefX: "x11g1kdw",
  k71WvV: "xnur1sd",
  "$$css": true
};
const flyoutStyles = {
  popover: {
    k7Eaqz: "x5w4yej",
    keTefX: "x11g1kdw",
    k71WvV: "xnur1sd",
    $$css: true
  },
  popoverCustomWidth: width => [_temp, {
    k7Eaqz: (typeof width === 'number' ? `${width}px` : width) != null ? "xkj4a21" : typeof width === 'number' ? `${width}px` : width,
    $$css: true
  }, {
    "--x-minWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof width === 'number' ? `${width}px` : width)
  }]
};
/**
 * A single menu row that reveals a nested flyout of its own items. The row
 * adopts DropdownMenuItem semantics (label / icon / description / isDisabled);
 * its `children` become the flyout content. Place inside a DropdownMenu (or
 * ContextMenu) alongside plain items.
 *
 * For data-driven menus, don't use this directly — give a menu item a nested
 * `items` array and DropdownMenu/ContextMenu renders the submenu for you.
 *
 * @example
 * ```
 * <DropdownMenu button={{label: 'Actions'}}>
 *   <DropdownMenuItem label="Rename" onClick={rename} />
 *   <DropdownMenuSubMenu label="Move to" icon="folder">
 *     <DropdownMenuItem label="Folder A" onClick={() => move('a')} />
 *     <DropdownMenuItem label="Folder B" onClick={() => move('b')} />
 *   </DropdownMenuSubMenu>
 * </DropdownMenu>
 * ```
 */
export function DropdownMenuSubMenu(props) {
  const {
    icon,
    label,
    description,
    isDisabled = false,
    hasSpinner = false,
    menuWidth,
    onOpenChange,
    children,
    xstyle,
    className,
    style,
    'data-testid': testId,
    menuDataTestId
  } = props;
  const menuCtx = useDropdownMenuContext();
  const menuSize = menuCtx?.menuSize ?? 'md';
  const canOpen = !isDisabled;
  const contentId = useId();
  const triggerId = useId();
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const layer = useLayer({
    mode: 'context',
    lightDismiss: false,
    onShow: useCallback(() => {
      setIsOpen(true);
      onOpenChange?.(true);
    }, [onOpenChange]),
    onHide: useCallback(() => {
      setIsOpen(false);
      onOpenChange?.(false);
    }, [onOpenChange])
  });
  const showLayer = useCallback(() => {
    if (canOpen) {
      layer.show();
    }
  }, [canOpen, layer]);
  const hideLayer = useCallback(() => {
    layer.hide();
  }, [layer]);

  // Dedicated roving-focus + typeahead for this flyout level. The boundary
  // selector scopes item collection and key handling to this flyout's own
  // `role="menu"` — so a submenu nested inside this one (also inline, also
  // `role="menu"`) doesn't pollute this level's items or double-handle keys.
  const {
    listRef: menuRef,
    handleKeyDown: listNavKeyDown,
    focusFirst,
    focusItem,
    ownsEvent,
    getItems
  } = useListFocus({
    itemSelector: MENU_ITEM_SELECTOR,
    boundarySelector: MENU_BOUNDARY_SELECTOR,
    wrap: false,
    onEscape: () => close({
      focusTrigger: true
    })
  });
  const typeahead = useTypeahead({
    getItemLabels: () => getItems().map(el => el.textContent),
    onMatch: focusItem,
    getCurrentIndex: () => getItems().findIndex(el => el === document.activeElement || el.contains(document.activeElement))
  });

  // Hover-intent: entering the trigger opens after a short delay; leaving
  // either surface closes after a delay. Hover-open does not steal focus.
  const {
    triggerProps,
    contentProps
  } = useMenuHover({
    show: showLayer,
    hide: hideLayer,
    isOpen,
    isEnabled: canOpen
  });
  const open = useCallback(options => {
    if (!canOpen) {
      return;
    }
    layer.show();
    if (options?.focusFirst) {
      requestAnimationFrame(() => {
        // Move focus into the flyout. When it has no focusable items yet
        // (e.g. an async submenu showing only a disabled "Loading…" row via
        // hasSpinner), focusFirst() finds nothing — fall back to focusing the
        // flyout container itself so keyboard ownership still transfers off
        // the parent list. Otherwise the parent would keep focus, letting
        // arrow keys rove the parent while the empty flyout stays open.
        const focusedItem = focusFirst();
        if (!focusedItem) {
          menuRef.current?.focus();
        }
      });
    }
  }, [canOpen, layer, focusFirst, menuRef]);
  const close = useCallback(options => {
    layer.hide();
    if (options?.focusTrigger !== false) {
      triggerRef.current?.focus();
    }
  }, [layer]);

  // Single ref for the trigger row: store it for focus management AND wire it
  // as the flyout's positioning anchor (CSS anchor positioning).
  const setTriggerEl = useCallback(el => {
    triggerRef.current = el;
    layer.ref(el);
  }, [layer]);
  const handleTriggerClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    // Click toggles the flyout, moving focus into it on open.
    if (isOpen) {
      close({
        focusTrigger: true
      });
    } else {
      open({
        focusFirst: true
      });
    }
  }, [isDisabled, isOpen, open, close]);
  const handleTriggerKeyDown = useCallback(e => {
    if (isDisabled) {
      return;
    }
    const isRtl = typeof window !== 'undefined' && triggerRef.current ? window.getComputedStyle(triggerRef.current).direction === 'rtl' : false;
    const openKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
    if (e.key === openKey || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // The trigger row is a direct child of the parent menu (not a nested
      // role="menu"), so the parent's key handler would otherwise also act on
      // this event (e.g. Enter clicking the focused row). Stop it here — this
      // level fully handles opening the flyout.
      e.stopPropagation();
      open({
        focusFirst: true
      });
    }
  }, [isDisabled, open]);

  // Move the single focus-driven highlight onto the trigger as the pointer
  // enters it, so a sibling item that still holds focus doesn't stay
  // highlighted alongside the hovered trigger. This is separate from the
  // hover-open intent (onMouseEnter/onMouseLeave) — the flyout still opens on
  // the hover delay; this only keeps the highlight single.
  const handlePointerMove = useCallback(e => focusMenuItemOnHover(e, isDisabled), [isDisabled]);

  // Enter/Space activate the focused row; typeahead jumps by first character;
  // the close key (Left, or Right in RTL) returns focus to the trigger;
  // arrows/Home/End defer to useListFocus (RTL-aware).
  //
  // The flyout renders inline (useLayer context mode is a native popover, not a
  // portal), so a submenu nested inside this one bubbles its key events up to
  // this handler. `ownsEvent` (from useListFocus, scoped by boundarySelector)
  // tells us whether the event originated in THIS flyout or a deeper one; we
  // only act on our own, letting the deeper level keep ownership. No manual
  // stopPropagation needed — each level self-filters.
  const handleContentKeyDown = useCallback(e => {
    if (!ownsEvent(e)) {
      return;
    }
    // Escape closes just this submenu and returns focus to its trigger (APG:
    // Escape collapses the current level, not the whole stack). The root
    // popover's focus-trap listens for Escape at the document level and bails
    // when the event is already handled, so we mark it handled here (stop
    // propagation + preventDefault) to keep the parent menu open.
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close({
        focusTrigger: true
      });
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const focused = document.activeElement;
      if (focused && MENU_ITEM_ROLES.has(focused.getAttribute('role') ?? '')) {
        focused.click();
      }
      return;
    }
    const isRtl = typeof window !== 'undefined' && menuRef.current ? window.getComputedStyle(menuRef.current).direction === 'rtl' : false;
    const closeKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === closeKey) {
      e.preventDefault();
      close({
        focusTrigger: true
      });
      return;
    }
    if (typeahead.onKeyDown(e)) {
      e.preventDefault();
      return;
    }
    listNavKeyDown(e);
  }, [ownsEvent, close, listNavKeyDown, typeahead, menuRef]);

  // Re-provide the menu context so nested items behave exactly like top-level
  // ones. Selecting a leaf item must dismiss the WHOLE stack, not just this
  // flyout: close this level (without stealing focus back to the trigger) and
  // propagate up via the parent menu's closeMenu. Because every level
  // re-provides the context, this chains from the deepest flyout all the way to
  // the root DropdownMenu (whose closeMenu hides the popover).
  const nestedMenuContext = useMemo(() => ({
    menuSize,
    closeMenu: () => {
      close({
        focusTrigger: false
      });
      menuCtx?.closeMenu();
    }
  }), [menuSize, close, menuCtx]);
  const endAffordance = hasSpinner ? /*#__PURE__*/_jsx("span", {
    ...mergeProps(themeProps('dropdown-menu-indicator-icon'), {
      className: "x78zum5 x6s0dn4"
    }),
    children: /*#__PURE__*/_jsx(Spinner, {
      size: "sm"
    })
  }) : /*#__PURE__*/_jsx("span", {
    ...mergeProps(themeProps('dropdown-menu-indicator-icon'), {
      className: "x78zum5 x6s0dn4"
    }),
    children: /*#__PURE__*/_jsx(Icon, {
      icon: "chevronRight",
      size: "sm",
      color: "secondary"
    })
  });
  const popoverXstyle = menuWidth ? flyoutStyles.popoverCustomWidth(menuWidth) : flyoutStyles.popover;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Item, {
      ref: el => setTriggerEl(el),
      id: triggerId,
      role: "menuitem",
      tabIndex: isDisabled ? undefined : -1,
      "aria-haspopup": "menu",
      "aria-expanded": isOpen,
      "aria-controls": isOpen ? contentId : undefined,
      "aria-disabled": isDisabled || undefined,
      "data-testid": testId,
      onMouseEnter: triggerProps.onMouseEnter,
      onMouseLeave: triggerProps.onMouseLeave,
      onPointerMove: handlePointerMove,
      startContent: icon ? renderIconSlot(icon, {
        size: 'sm',
        color: 'secondary'
      }) : undefined,
      label: label,
      description: description,
      endContent: endAffordance,
      onClick: handleTriggerClick,
      onKeyDown: handleTriggerKeyDown,
      isDisabled: isDisabled,
      xstyle: [triggerStyles.root, triggerSizeStyles[menuSize], isOpen && triggerStyles.open, isDisabled && triggerStyles.disabled, xstyle],
      ...mergeProps(themeProps('dropdown-menu-item', {
        size: menuSize
      }), {
        className,
        style
      })
    }), layer.render(/*#__PURE__*/_jsx("div", {
      ref: menuRef,
      id: contentId,
      role: "menu"
      // Focusable as a fallback target so an empty/loading flyout (e.g.
      // hasSpinner with only a disabled row) can still receive keyboard
      // focus and own arrow/Escape keys instead of leaving focus on the
      // parent list.
      ,
      tabIndex: -1,
      "aria-labelledby": triggerId,
      onKeyDown: handleContentKeyDown,
      onMouseEnter: contentProps.onMouseEnter,
      onMouseLeave: contentProps.onMouseLeave,
      "data-testid": menuDataTestId,
      ...mergeProps(themeProps('dropdown-menu'), {
        className: "x9f619 x78zum5 xdt5ytf x1lsbc85 xuyqlj2 x1odjw0f x1fcsqxe xgory14 x9epnlk x1n97fys x1prclbq x1i5ehqx x1hc1fzr x19991ni xuedmi6 xlr8y92"
      }),
      children: /*#__PURE__*/_jsx(DropdownMenuContext, {
        value: nestedMenuContext,
        children: children
      })
    }), {
      placement: 'end',
      alignment: 'start',
      xstyle: [popoverXstyle, layerAnimations.end]
    })]
  });
}
DropdownMenuSubMenu.displayName = 'DropdownMenuSubMenu';